import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/cache/queryClient';
import { searchRecipes } from '@/services/api/recipes';

interface UseSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
  limit?: number;
}

export function useSearch(options: UseSearchOptions = {}) {
  const { debounceMs = 300, minQueryLength = 2, limit = 20 } = options;
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  const shouldSearch = debouncedQuery.length >= minQueryLength;

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.search(debouncedQuery),
    queryFn: () => searchRecipes(debouncedQuery, limit),
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds for search results
  });

  const results = useMemo(() => {
    if (!shouldSearch) return [];
    return data || [];
  }, [data, shouldSearch]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading: isLoading && shouldSearch,
    error,
    clearSearch,
    hasResults: results.length > 0,
    isSearching: shouldSearch,
  };
}

export function useKeyboardNavigation<T>(
  items: T[],
  onSelect: (item: T) => void,
  isActive: boolean
) {
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [items]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isActive || items.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < items.length) {
            onSelect(items[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setSelectedIndex(-1);
          break;
      }
    },
    [isActive, items, selectedIndex, onSelect]
  );

  useEffect(() => {
    if (isActive) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isActive, handleKeyDown]);

  return {
    selectedIndex,
    setSelectedIndex,
  };
}
